import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { X, MessageCircle } from "lucide-react";

import { fetchArtworkDetail, fetchArtistProfile } from "../../services/arthouseApi";
import { useArtists } from "../../context/ArtistContext";

/* -----------------------
   small helpers
------------------------ */
function safeText(v, fallback = "") {
  return (v ?? "").toString().trim() || fallback;
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function getArtworkImage(a) {
  return (
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    ""
  );
}

function getArtistId(a) {
  return a?.id ?? a?.artist_id ?? a?.artistId ?? null;
}

function pickComments(payload) {
  // Supports a few common shapes:
  // - data.comments
  // - artwork.comments
  // - artwork.comment_list
  // - artwork.recent_comments
  const a = payload?.artwork ?? payload ?? null;
  return (
    safeArr(payload?.comments) ||
    safeArr(a?.comments) ||
    safeArr(a?.comment_list) ||
    safeArr(a?.recent_comments) ||
    []
  );
}

function getCommentId(c, i) {
  return (
    c?.comment_id || // ArtworkPage shape
    c?.id ||
    `${c?.user_id ?? c?.username ?? "u"}-${c?.created_at ?? "t"}-${i}`
  );
}

function getCommentText(c) {
  return safeText(
    c?.comment_text ?? // ArtworkPage shape
      c?.text ??
      c?.body ??
      c?.content ??
      c?.message,
    ""
  );
}

function getCommentTime(c) {
  return safeText(
    c?.created_at ?? // ArtworkPage shape
      c?.createdAt ??
      c?.timestamp,
    ""
  );
}

function getCommentAuthor(c) {
  const name =
    safeText(c?.user?.display_name) ||
    safeText(c?.user?.displayName) ||
    safeText(c?.author?.display_name) ||
    safeText(c?.author?.displayName) ||
    safeText(c?.username) || // ArtworkPage shape
    safeText(c?.handle) ||
    safeText(c?.author_name) ||
    safeText(c?.user_name);

  const handle =
    safeText(c?.user?.username) ||
    safeText(c?.author?.username) ||
    safeText(c?.user_username) ||
    safeText(c?.author_username);

  return {
    name: name || (handle ? `@${String(handle).replace(/^@/, "")}` : "User"),
    handle: handle ? `@${String(handle).replace(/^@/, "")}` : "",
    avatar:
      safeText(c?.profile_picture) || // ArtworkPage shape
      safeText(c?.user?.avatar_url) ||
      safeText(c?.author?.avatar_url) ||
      safeText(c?.user_avatar_url) ||
      safeText(c?.author_avatar_url) ||
      "",
  };
}

/* -----------------------
   compact artist header (right panel)
------------------------ */
function CompactArtistHeader({ artist }) {
  const id = getArtistId(artist);
  const name = safeText(artist?.display_name || artist?.displayName, "Artist");
  const bio = safeText(artist?.bio, "");
  const avatar = safeText(artist?.avatar_url || artist?.avatarUrl, "");

  return (
    <div className="rounded-[22px] border border-white/12 bg-black/15 p-4">
      <div className="text-[10px] tracking-[0.35em] uppercase text-white/45">
        Artist
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-10 w-10 rounded-full border border-white/12 object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-10 w-10 rounded-full border border-white/12 bg-white/[0.06]" />
          )}

          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white/90">
              {name}
            </div>
            {bio ? (
              <div className="truncate text-xs text-white/55">{bio}</div>
            ) : (
              <div className="text-xs text-white/40">Creator</div>
            )}
          </div>
        </div>

        {id ? (
          <Link
            to={`/arthouse/artist/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="
              shrink-0 inline-flex items-center justify-center
              h-9 px-4 rounded-full
              border border-white/12 bg-white/[0.05]
              text-sm font-semibold text-white/80
              hover:bg-white/[0.08] transition
            "
          >
            View →
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/* -----------------------
   comments panel (view-only)
------------------------ */
function CommentsPanel({ comments = [], loading = false }) {
  const items = useMemo(() => safeArr(comments).slice(0, 18), [comments]);

  return (
    <div className="rounded-[22px] border border-white/12 bg-black/15 overflow-hidden flex flex-col min-h-0">
      <div className="px-4 pt-4 pb-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-white/85">
          <MessageCircle size={16} className="opacity-80" />
          <div className="text-sm font-semibold">Comments</div>
        </div>
        <div className="text-xs text-white/45">{items.length}</div>
      </div>

      {/* list (internal scroll so modal itself stays clean) */}
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        {loading ? (
          <div className="rounded-[18px] border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
            Loading comments…
          </div>
        ) : items.length ? (
          <div className="grid gap-3">
            {items.map((c, i) => {
              const author = getCommentAuthor(c);
              const text = getCommentText(c);
              const t = getCommentTime(c);

              if (!text) return null;

              return (
                <div
                  key={getCommentId(c, i)}
                  className="
                    rounded-[18px] border border-white/10
                    bg-white/[0.03] px-3.5 py-3
                    hover:bg-white/[0.05] transition
                  "
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {author.avatar ? (
                      <img
                        src={author.avatar}
                        alt=""
                        className="h-8 w-8 rounded-full border border-white/12 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full border border-white/12 bg-white/[0.06]" />
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="truncate text-sm font-semibold text-white/85">
                          {author.name}
                        </div>
                        {author.handle ? (
                          <div className="truncate text-xs text-white/40">
                            {author.handle}
                          </div>
                        ) : null}
                      </div>

                      {t ? (
                        <div className="text-[11px] text-white/35 truncate">
                          {t}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-2 text-[13px] leading-snug text-white/70">
                    {text}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-[18px] border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
            No comments yet.
            <div className="mt-1 text-xs text-white/40">
              Be the first to leave one on the artwork page.
            </div>
          </div>
        )}
      </div>

      {/* footer hint */}
      <div className="px-4 py-3 border-t border-white/10 text-[11px] text-white/40">
        View-only preview. Open the artwork to write a comment.
      </div>
    </div>
  );
}

/* -----------------------
   modal
------------------------ */
export default function ArtistMiniCardModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { artistId } = useParams();

  const { upsertArtist, getArtist } = useArtists();

  const snapshot = location.state?.artwork || null;
  const artworkId = getArtworkId(snapshot);

  const [loading, setLoading] = useState(true);
  const [fallbackArtist, setFallbackArtist] = useState(null);
  const [data, setData] = useState(null);

  // DB-backed comments (same source as ArtworkPage)
  const [dbComments, setDbComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const close = () => navigate(-1, { replace: true });

  // Go to the full artwork page (best place for full comments + actions)
  const goToArtworkPage = () => {
    const targetId = getArtworkId(data?.artwork) || artworkId;
    if (!targetId) return;

    navigate(`/arthouse/art/${targetId}`, {
      replace: true,
      state: { artwork: data?.artwork || snapshot },
    });
  };

  // ESC closes
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // lock background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, []);

  // load full detail (artist/artwork)
  useEffect(() => {
    let alive = true;
    setLoading(true);

    const load = async () => {
      try {
        let nextData = null;

        if (artworkId) {
          nextData = await fetchArtworkDetail(artworkId);
          if (nextData?.artwork?.artist) upsertArtist(nextData.artwork.artist);
        }

        let nextArtist = null;
        if (!nextData?.artwork?.artist && artistId) {
          try {
            const a = await fetchArtistProfile(artistId);
            nextArtist = a?.artist ?? a ?? null;
            if (nextArtist) upsertArtist(nextArtist);
          } catch {}
        }

        if (!alive) return;
        setData(nextData);
        setFallbackArtist(nextArtist);
      } catch {
        if (!alive) return;
        setData(null);
        setFallbackArtist(null);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [artworkId, artistId, upsertArtist]);

  // Fetch comments from DB (same endpoint as ArtworkPage)
  useEffect(() => {
    const targetId = getArtworkId(data?.artwork) || artworkId;
    if (!targetId) return;

    let alive = true;

    (async () => {
      setLoadingComments(true);
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || "/api";
        const response = await fetch(`${API_BASE}/comments/artwork/${targetId}`, {
          credentials: "include",
        });

        if (!alive) return;

        if (response.ok) {
          const json = await response.json();
          setDbComments(Array.isArray(json?.comments) ? json.comments : []);
        } else {
          setDbComments([]);
        }
      } catch (err) {
        if (alive) setDbComments([]);
        console.error("Error fetching comments (modal):", err);
      } finally {
        if (alive) setLoadingComments(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [artworkId, data]);

  const artwork = data?.artwork || snapshot || null;
  const rawArtist = artwork?.artist || fallbackArtist || null;
  const liveArtist =
    getArtist(artwork?.artist?.id) || getArtist(rawArtist?.id) || rawArtist;

  const img = useMemo(() => getArtworkImage(artwork), [artwork]);
  const title = safeText(artwork?.title, "Untitled");
  const desc = safeText(artwork?.description || artwork?.caption || artwork?.subtitle, "");

  // Prefer DB comments; fallback to embedded payload comments if any
  const comments = useMemo(() => {
    if (dbComments.length) return dbComments;
    return pickComments(data || artwork || null);
  }, [dbComments, data, artwork]);

  return (
    <div className="fixed inset-0 z-[999]">
      <button
        type="button"
        onClick={close}
        className="absolute inset-0 bg-black/55 backdrop-blur-[8px]"
        aria-label="Close"
      />

      <div className="relative h-full w-full flex items-center justify-center p-4 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          className="
            relative w-full
            max-w-[1180px]
            h-[680px] sm:h-[720px]
            rounded-[32px]
            border border-white/12
            bg-white/[0.045]
            backdrop-blur-2xl
            shadow-[0_40px_170px_rgba(0,0,0,0.75)]
            overflow-hidden
          "
        >
          {/* ambient glow (subtle) */}
          <div className="pointer-events-none absolute -inset-24">
            <div className="absolute -top-24 left-1/2 h-64 w-[980px] -translate-x-1/2 rotate-6 bg-sky-300/10 blur-[70px]" />
            <div className="absolute -bottom-28 right-[-160px] h-72 w-[680px] bg-purple-400/10 blur-[85px]" />
          </div>

          {/* header */}
          <div className="relative flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.35em] uppercase text-white/45">
                Creator Preview
              </div>
              <div className="mt-1 truncate text-base sm:text-lg font-semibold text-white/90">
                {liveArtist?.display_name || "Artist"}
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              className="
                h-10 w-10 rounded-full
                border border-white/12 bg-white/[0.04]
                hover:bg-white/[0.08]
                transition grid place-items-center
                text-white/85
              "
              aria-label="Close"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* body */}
          <div className="relative h-[calc(100%-72px)] p-5 sm:p-6">
            {loading ? (
              <div className="h-full rounded-[26px] border border-white/12 bg-white/[0.04] grid place-items-center text-white/70">
                Loading…
              </div>
            ) : !artwork ? (
              <div className="h-full rounded-[26px] border border-white/12 bg-white/[0.04] grid place-items-center text-white/70">
                Couldn’t load artwork.
              </div>
            ) : (
              <div className="h-full grid grid-cols-1 md:grid-cols-[1.6fr_0.9fr] gap-5">
                {/* LEFT: ARTWORK (dominant ~75%) */}
                <div className="h-full rounded-[26px] border border-white/12 bg-black/25 overflow-hidden">
                  <div className="relative h-full bg-black/30">
                    {img ? (
                      <img
                        src={img}
                        alt={title}
                        className="absolute inset-0 h-full w-full object-cover opacity-[0.99]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[radial-gradient(900px_520px_at_30%_10%,rgba(56,189,248,0.16),transparent_60%),radial-gradient(900px_520px_at_90%_30%,rgba(168,85,247,0.12),transparent_55%)]" />
                    )}

                    {/* cinematic overlay */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

                    {/* bottom caption card */}
                    <div className="absolute left-4 right-4 bottom-4">
                      <div className="rounded-[22px] border border-white/12 bg-black/30 backdrop-blur-2xl p-4">
                        <div className="truncate text-base font-semibold text-white/92">
                          {title}
                        </div>

                        {desc ? (
                          <div className="mt-1.5 line-clamp-2 text-[12px] leading-snug text-white/60">
                            {desc}
                          </div>
                        ) : null}

                        <div className="mt-3 flex items-center gap-4 text-[12px] text-white/70">
                          <span className="inline-flex items-center gap-2">
                            <span className="opacity-80">⟡</span>
                            {artwork?.views ?? "—"}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <span className="opacity-80">♡</span>
                            {artwork?.likes ?? "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: artist + comments (no merch/links) */}
                <div className="h-full flex flex-col gap-4 min-h-0">
                  <CompactArtistHeader artist={liveArtist} />

                  <CommentsPanel comments={comments} loading={loadingComments} />

                  <div className="mt-auto flex items-center justify-end">
                    <button
                      type="button"
                      onClick={goToArtworkPage}
                      disabled={!getArtworkId(data?.artwork) && !artworkId}
                      className="
                        h-11 px-5 rounded-2xl
                        border border-emerald-400/20 bg-emerald-400/10
                        hover:bg-emerald-400/15
                        transition text-sm font-semibold text-emerald-200
                        disabled:opacity-40 disabled:cursor-not-allowed
                      "
                    >
                      View Artwork →
                    </button>
                  </div>

                  {!artworkId ? (
                    <div className="text-xs text-white/40">
                      Tip: pass{" "}
                      <span className="text-white/55">state.artwork.id</span> when
                      opening the modal so comments load from DB.
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </div>
    </div>
  );
}
